import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{title}</h2>
    <div className="space-y-4 text-gray-700 dark:text-gray-300">{children}</div>
  </section>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc list-outside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto">
        <article className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Terms and Conditions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: January 3, 2026</p>

          <div className="max-w-none">
            <p className="text-gray-700 dark:text-gray-300">
              Welcome to Nexussales.io LLC ("Nexussales.io," "we," "our," or "us"). These Terms and
              Conditions ("Terms") govern your access to and use of the Nexussales.io platform, websites,
              software, communications tools, billing tools, support services, and related products
              (collectively, the "Services"). By creating an account, accessing, purchasing, or using the
              Services, you agree to these Terms. If you do not agree, do not use the Services.
            </p>

            <Section title="1. Business Use and Eligibility">
              <p>
                The Services are intended for business use only. You must be at least 18 years old and have
                authority to bind the business or organization you represent. You are responsible for ensuring
                all account information is accurate, current, and complete.
              </p>
            </Section>

            <Section title="2. Account Security and Authorized Users">
              <p>
                You are responsible for all activity under your account and for maintaining the confidentiality
                of login credentials, API keys, user permissions, devices, and connected third-party services.
                You must promptly notify us of unauthorized access or suspected security incidents.
              </p>
              <BulletList
                items={[
                  'You may only grant access to authorized personnel who need the Services for legitimate business purposes.',
                  'You are responsible for actions taken by your users, employees, contractors, agents, and administrators.',
                  'We may suspend access when we reasonably believe an account creates security, fraud, legal, provider, or platform risk.',
                ]}
              />
            </Section>

            <Section title="3. Services">
              <p>
                Nexussales.io provides customer relationship management, lead management, workflow automation,
                messaging, voice, analytics, document, and billing features. We may add, change, limit, suspend,
                or discontinue features at any time. Some features depend on third-party providers, carrier
                approval, network availability, app stores, payment processors, or integrated services.
              </p>
            </Section>

            <Section title="4. Subscriptions, Fees, and Payment">
              <p>
                The Services may include recurring subscriptions, usage-based charges, add-ons, phone-number
                charges, carrier fees, taxes, pass-through costs, wallet refills, and other paid features. By
                starting a subscription, adding a payment method, funding a wallet, purchasing an add-on, buying
                or using phone numbers, or continuing to use paid features, you authorize Nexussales.io and its
                payment processors to charge your payment method for all applicable fees.
              </p>
              <BulletList
                items={[
                  <span>
                    <strong>No refunds.</strong> All payments, subscriptions, setup fees, usage charges, wallet
                    refills, phone-number charges, add-ons, taxes, pass-through costs, and other fees are
                    non-refundable to the fullest extent permitted by law, including when you cancel, downgrade,
                    do not use the Services, lose provider approval, or your account is suspended or terminated.
                  </span>,
                  <span>
                    <strong>Cancellation by contact only.</strong> To cancel a subscription or paid add-on, you
                    must contact us at{' '}
                    <a href="mailto:info@nexussales.io" className="text-blue-600 dark:text-blue-400 hover:underline">
                      info@nexussales.io
                    </a>
                    . Cancellation requests are not complete until processed by us. Unless we state otherwise in
                    writing, cancellation stops future renewal charges but does not refund amounts already paid
                    or incurred.
                  </span>,
                  'You remain responsible for charges incurred before cancellation is processed, including usage, carrier, provider, phone-number, payment failure, chargeback, collection, tax, and pass-through costs.',
                  'We may change pricing, plans, included features, usage rates, billing terms, and provider pass-through charges with notice where required by law.',
                  'If payment fails, we may retry payment, suspend or limit access, freeze outbound messaging, downgrade features, remove phone numbers, or terminate the account.',
                ]}
              />
            </Section>

            <Section title="5. Communications Compliance">
              <p>
                You are solely responsible for your communications, campaigns, contact lists, consent records,
                message content, call activity, and compliance with all laws, rules, industry standards, and
                provider requirements that apply to your use of the Services.
              </p>
              <BulletList
                items={[
                  'You must obtain, document, and maintain all required consent before sending SMS, MMS, email, voice, automated, prerecorded, marketing, or other communications.',
                  'You must comply with the TCPA, CAN-SPAM, CTIA guidelines, carrier rules, A2P 10DLC requirements, Do Not Call rules, opt-out obligations, quiet-hour rules, and all similar federal, state, local, and international laws that apply to your business.',
                  'You must honor STOP, unsubscribe, revocation, opt-out, and do-not-contact requests promptly and must not bypass platform safeguards.',
                  'You are responsible for A2P brand and campaign information, message-flow disclosures, privacy and terms links, sample messages, sender identity, prohibited content, and ongoing campaign compliance.',
                  'You are solely liable for fines, penalties, carrier fees, provider fees, claims, losses, and damages caused by your communications or non-compliance.',
                ]}
              />
            </Section>

            <Section title="6. Acceptable Use">
              <p>You agree not to use the Services to:</p>
              <BulletList
                items={[
                  'Send spam, deceptive, fraudulent, unlawful, harassing, abusive, threatening, defamatory, discriminatory, or misleading communications.',
                  'Promote illegal goods or services, regulated content without required approvals, phishing, scams, malware, credential theft, or harmful activity.',
                  'Upload, store, transmit, or request sensitive personal information unless you have a lawful basis and appropriate safeguards.',
                  'Scrape, probe, overload, interfere with, reverse engineer, or attempt unauthorized access to the Services or connected systems.',
                  'Use the Services in a way that could damage Nexussales.io, our providers, carriers, customers, reputation, infrastructure, or legal standing.',
                ]}
              />
            </Section>

            <Section title="7. Customer Data and Privacy">
              <p>
                You retain ownership of business data, lead data, contact data, messages, files, and other
                content you submit to the Services ("Customer Data"). You grant us the rights necessary to host,
                process, transmit, display, secure, back up, analyze, and use Customer Data to provide, support,
                protect, and improve the Services. Our handling of personal information is described in our{' '}
                <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
              <BulletList
                items={[
                  'You represent that you have all rights, notices, consents, and legal bases required to provide Customer Data to us and use it through the Services.',
                  'You are responsible for the accuracy, legality, retention, deletion, export, and use of Customer Data.',
                  'We do not sell Customer Data, but we may share it with service providers as needed to operate, secure, bill, support, and improve the Services.',
                ]}
              />
            </Section>

            <Section title="8. Third-Party Services and Providers">
              <p>
                The Services rely on third parties such as Supabase, Stripe, Telnyx, cloud hosting, email,
                analytics, telecom carriers, payment networks, and other integrations. We are not responsible for
                third-party outages, policy changes, fees, denials, delays, filtering, number recycling, campaign
                rejection, payment holds, provider errors, or third-party terms. Your use of third-party services
                may be subject to their separate terms and policies.
              </p>
            </Section>

            <Section title="9. Intellectual Property">
              <p>
                Nexussales.io and its licensors own all rights in the Services, software, interfaces, workflows,
                designs, documentation, trademarks, trade names, and related technology. Subject to these Terms,
                we grant you a limited, revocable, non-exclusive, non-transferable license to use the Services
                for your internal business operations. No rights are granted except as expressly stated.
              </p>
            </Section>

            <Section title="10. Beta Features and Support">
              <p>
                We may offer previews, beta features, experiments, or early access functionality. These features
                may be changed or discontinued at any time and may be less reliable than generally available
                functionality. Support availability, response times, fixes, and feature requests are not
                guaranteed unless stated in a separate written agreement signed by us.
              </p>
            </Section>

            <Section title="11. Suspension and Termination">
              <p>
                We may suspend, restrict, or terminate access immediately if we believe your use violates these
                Terms, applicable law, provider requirements, payment obligations, acceptable-use rules, security
                standards, or creates risk for Nexussales.io, users, providers, carriers, or the public. Upon
                termination, your right to use the Services ends. You remain responsible for all amounts due and
                for obligations that by their nature should survive termination.
              </p>
            </Section>

            <Section title="12. Disclaimers">
              <p>
                The Services are provided "as is" and "as available." To the fullest extent permitted by law, we
                disclaim all warranties, express or implied, including warranties of merchantability, fitness for
                a particular purpose, non-infringement, availability, accuracy, security, deliverability, carrier
                approval, campaign approval, message delivery, revenue results, compliance outcomes, or
                uninterrupted operation. You use the Services at your own risk.
              </p>
            </Section>

            <Section title="13. Limitation of Liability">
              <p>
                To the fullest extent permitted by law, Nexussales.io and its owners, officers, employees,
                contractors, affiliates, licensors, and providers will not be liable for indirect, incidental,
                special, consequential, exemplary, punitive, or lost-profit damages, or for loss of data,
                goodwill, revenue, business, customers, leads, deliverability, phone numbers, provider access, or
                communications capability.
              </p>
              <p>
                To the fullest extent permitted by law, our total aggregate liability for all claims related to
                the Services will not exceed the amounts you paid to Nexussales.io for the Services giving rise
                to the claim during the three (3) months before the event giving rise to liability.
              </p>
            </Section>

            <Section title="14. Indemnification">
              <p>
                You agree to defend, indemnify, and hold harmless Nexussales.io and its owners, officers,
                employees, contractors, affiliates, licensors, and providers from and against all claims,
                liabilities, damages, losses, fines, penalties, costs, and expenses, including reasonable
                attorneys' fees, arising from or related to:
              </p>
              <BulletList
                items={[
                  'Your use or misuse of the Services.',
                  'Your Customer Data, campaigns, messages, calls, contacts, lists, consent practices, or business activities.',
                  'Your violation of these Terms, applicable law, provider rules, carrier rules, third-party rights, or payment obligations.',
                  'Claims from your customers, leads, users, employees, contractors, regulators, carriers, providers, or payment processors.',
                ]}
              />
            </Section>

            <Section title="15. Governing Law and Disputes">
              <p>
                These Terms are governed by the laws of the State of Florida and applicable United States law,
                without regard to conflict-of-law rules. Unless prohibited by law, any dispute must be brought in
                the state or federal courts located in Florida, and you consent to the jurisdiction and venue of
                those courts.
              </p>
            </Section>

            <Section title="16. Changes to These Terms">
              <p>
                We may update these Terms from time to time. Updated Terms will be posted on our website or made
                available through the Services. Your continued use of the Services after an update means you
                accept the updated Terms.
              </p>
            </Section>

            <Section title="17. Contact Information">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <p>If you have questions about these Terms, billing, or cancellation, please contact:</p>
                <p>
                  <strong>Nexussales.io LLC</strong>
                  <br />
                  Email:{' '}
                  <a href="mailto:info@nexussales.io" className="text-blue-600 dark:text-blue-400 hover:underline">
                    info@nexussales.io
                  </a>
                  <br />
                  Website:{' '}
                  <a
                    href="https://crm.nexussales.io"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    crm.nexussales.io
                  </a>
                </p>
              </div>
            </Section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default Terms;
